import React, { useCallback, useRef, useState } from "react";

export interface ResizableWidthOptions {
  // The width (px) without a stored one, and the one a reset returns to
  defaultWidth: number;
  min: number;
  max: number;
  // The localStorage key the width is kept under, per browser
  storageKey: string;
  // Px a press of an arrow key moves the width
  keyStep?: number;
}

// What the hook hands to the handle that resizes: the width, and the
// props to spread onto it
export interface ResizableWidth {
  width: number;
  min: number;
  max: number;
  handleProps: {
    onPointerDown: (event: React.PointerEvent<HTMLElement>) => void;
    onPointerMove: (event: React.PointerEvent<HTMLElement>) => void;
    onPointerUp: (event: React.PointerEvent<HTMLElement>) => void;
    onPointerCancel: (event: React.PointerEvent<HTMLElement>) => void;
    onKeyDown: (event: React.KeyboardEvent<HTMLElement>) => void;
    onDoubleClick: () => void;
  };
  dragging: boolean;
}

const clamp = (value: number, min: number, max: number): number =>
  Math.min(max, Math.max(min, value));

// The stored width, or null where there is none, it is no number, or the
// storage cannot be read
const readStoredWidth = (storageKey: string): number | null => {
  try {
    const raw = window.localStorage.getItem(storageKey);
    if (raw === null) {
      return null;
    }
    const value = Number(raw);
    return Number.isFinite(value) ? value : null;
  } catch {
    return null;
  }
};

const storeWidth = (storageKey: string, width: number): void => {
  try {
    window.localStorage.setItem(storageKey, String(width));
  } catch {
    // Storage may be full or forbidden; the width then lasts the session
  }
};

const forgetWidth = (storageKey: string): void => {
  try {
    window.localStorage.removeItem(storageKey);
  } catch {
    // Nothing stored that could be forgotten
  }
};

// A width the user may change by dragging a handle or with the arrow keys,
// kept within a range and remembered in the browser. Dragging uses pointer
// capture, so it follows the pointer over the results without selecting text.
export const useResizableWidth = ({
  defaultWidth,
  min,
  max,
  storageKey,
  keyStep = 16,
}: ResizableWidthOptions): ResizableWidth => {
  const [width, setWidth] = useState(() =>
    clamp(readStoredWidth(storageKey) ?? defaultWidth, min, max),
  );
  const [dragging, setDragging] = useState(false);
  // Where the pointer and the width stood when the drag began
  const dragStart = useRef<{ x: number; width: number } | null>(null);

  const update = useCallback(
    (next: number) => {
      const clamped = clamp(next, min, max);
      setWidth(clamped);
      storeWidth(storageKey, clamped);
    },
    [min, max, storageKey],
  );

  const onPointerDown = (event: React.PointerEvent<HTMLElement>) => {
    if (event.button !== 0) {
      return;
    }
    event.preventDefault();
    event.currentTarget.setPointerCapture?.(event.pointerId);
    dragStart.current = { x: event.clientX, width };
    setDragging(true);
  };

  const onPointerMove = (event: React.PointerEvent<HTMLElement>) => {
    const start = dragStart.current;
    if (start) {
      update(start.width + event.clientX - start.x);
    }
  };

  const endDrag = (event: React.PointerEvent<HTMLElement>) => {
    if (!dragStart.current) {
      return;
    }
    dragStart.current = null;
    setDragging(false);
    event.currentTarget.releasePointerCapture?.(event.pointerId);
  };

  const onKeyDown = (event: React.KeyboardEvent<HTMLElement>) => {
    if (event.key === "ArrowLeft") {
      event.preventDefault();
      update(width - keyStep);
    } else if (event.key === "ArrowRight") {
      event.preventDefault();
      update(width + keyStep);
    }
  };

  const onDoubleClick = () => {
    setWidth(clamp(defaultWidth, min, max));
    forgetWidth(storageKey);
  };

  return {
    width,
    min,
    max,
    dragging,
    handleProps: {
      onPointerDown,
      onPointerMove,
      onPointerUp: endDrag,
      onPointerCancel: endDrag,
      onKeyDown,
      onDoubleClick,
    },
  };
};
